import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  Stack,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  History as HistoryIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import ServiceHistoryDataService from '../../services/serviceHistory.service';
import ServiceDataService from '../../services/service.service';
import ClientService from '../../services/client.service';
import { formatCurrencySync, initializeCurrency } from '../../utils/currencyUtils';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const ServiceHistoryList = ({ onCreate }) => {
  const [serviceHistories, setServiceHistories] = useState([]);
  const [filteredHistories, setFilteredHistories] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  useEffect(() => {
    // Initialize currency settings first
    initializeCurrency().then(() => {
      loadData();
    });
  }, []);

  useEffect(() => {
    filterHistories();
  }, [serviceHistories, searchTerm, serviceFilter, clientFilter, dateFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load all data in parallel
      const [historiesResponse, servicesResponse, clientsResponse] = await Promise.all([
        ServiceHistoryDataService.getAll(),
        ServiceDataService.getAll(),
        ClientService.getClients()
      ]);

      if (historiesResponse.data.success) {
        setServiceHistories(historiesResponse.data.data);
      } else {
        setServiceHistories(historiesResponse.data || []);
      }

      if (servicesResponse.data.success) {
        setServices(servicesResponse.data.data);
      } else {
        setServices(servicesResponse.data || []);
      }

      if (clientsResponse.data.success) {
        setClients(clientsResponse.data.data);
      } else {
        setClients(clientsResponse.data?.data || []);
      }

    } catch (error) {
      setError('Failed to load service history data');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterHistories = () => {
    let filtered = serviceHistories;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(history =>
        history.service?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        history.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        history.id.toString().includes(searchTerm)
      );
    }

    // Service filter
    if (serviceFilter !== 'all') {
      filtered = filtered.filter(history => history.service_id == serviceFilter);
    }

    // Client filter
    if (clientFilter !== 'all') {
      filtered = filtered.filter(history => history.client_id == clientFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(history => {
        const historyDate = new Date(history.fecha);
        
        switch (dateFilter) {
          case 'today':
            return historyDate >= today;
          case 'week':
            const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
            return historyDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            return historyDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    setFilteredHistories(filtered);
  };


  const formatCurrency = (amount) => {
    return formatCurrencySync(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <HistoryIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
          {t ('serviceHistory.title')}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
        {t ('serviceHistory.msgtrack')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters and Actions */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder={t ('serviceHistory.searchHistory')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Service</InputLabel>
                <Select
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                  label={t ('serviceHistory.service')}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">{t ('serviceHistory.allservice')}</MenuItem>
                  {services.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>{t ('serviceHistory.client')}</InputLabel>
                <Select
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  label="Client"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">{t ('serviceHistory.allclients')}</MenuItem>
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>{t ('common.date')}</InputLabel>
                <Select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  label="Date"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">{t ('common.date')}</MenuItem>
                  <MenuItem value="today">{t ('serviceHistory.today')}</MenuItem>
                  <MenuItem value="week">{t ('serviceHistory.tweek')}</MenuItem>
                  <MenuItem value="month">{t ('serviceHistory.tmonth')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setSearchTerm('');
                    setServiceFilter('all');
                    setClientFilter('all');
                    setDateFilter('all');
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  {t ('serviceHistory.clearfilters')}
                </Button>

              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Service History Table */}
      <Card sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell><strong>ID</strong></TableCell>
                <TableCell><strong> {t ('serviceHistory.service')}</strong></TableCell>
                <TableCell><strong> {t ('serviceHistory.client')}</strong></TableCell>
                <TableCell><strong>{t ('common.date')}</strong></TableCell>
                <TableCell><strong>{t ('serviceHistory.cost')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHistories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <HistoryIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                      {t ('serviceHistory.noHistoryFound')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {searchTerm || serviceFilter !== 'all' || clientFilter !== 'all' || dateFilter !== 'all'
                          ? 'Try adjusting your filters'
                          : t ('serviceHistory.createFirstService')}
                      </Typography>
                      {!searchTerm && serviceFilter === 'all' && clientFilter === 'all' && dateFilter === 'all' && hasPermission('service-history.create') && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={onCreate}
                          sx={{
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            }
                          }}
                        >
                         {t ('serviceHistory.createRecord')}
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredHistories.map((history) => (
                  <TableRow key={history.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                          {history.id}
                        </Avatar>
                        <Typography variant="body2" fontWeight="bold">
                          #{history.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {history.service?.name || 'Unknown Service'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="body2" fontWeight="medium">
                          {history.client?.name || 'Unknown Client'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CalendarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="body2">
                          {formatDate(history.fecha)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>

                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {formatCurrency(history.costo || 0)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

    </Box>
  );
};

export default ServiceHistoryList;
